import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import Swal from 'sweetalert2';
import { PromocodeService } from '../../../../../services/promo-code';

interface ExpirationCalendarDay {
  date: Date;
  dayNumber: string;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

@Component({
  selector: 'app-promocodemobile',
  templateUrl: './promocodemobile.component.html',
  styleUrl: './promocodemobile.component.scss'
})
export class PromocodemobileComponent implements OnInit {
  allData: any[] = [];
  visibleData: any[] = [];

  contentReady = true;
  noData = false;
  isAr = localStorage.getItem('language') === 'ar';

  promocodeForm!: FormGroup;

  pageSize = 10;
  currentPage = 1;
  hasMore = false;
  isLoadingMore = false;

  showExpirationCalendar: boolean = false;
  expirationCalendarViewDate: Date = new Date();

  constructor(
    private promoService: PromocodeService,
    private fb: FormBuilder,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadPromocodes();
  }

  initForm(): void {
    this.promocodeForm = this.fb.group({
      code: ['', Validators.required],
      type: ['1', Validators.required],
      percentage: [''],
      expiration_date: [''],
      active: [true]
    });

    this.syncExpirationCalendarView();
  }

  loadPromocodes(): void {
    this.contentReady = true;

    this.promoService.getAll().subscribe({
      next: (data) => {
        this.allData = Array.isArray(data) ? data : [];

        this.currentPage = 1;
        this.visibleData = this.allData.slice(0, this.pageSize);

        this.noData = this.allData.length === 0;
        this.hasMore = this.visibleData.length < this.allData.length;

        this.contentReady = false;
      },
      error: (err) => {
        console.error(err);
        this.contentReady = false;
      }
    });
  }

  addCode(): void {
    if (this.promocodeForm.invalid) {
      this.promocodeForm.markAllAsTouched();
      return;
    }

    const payload = {
      ...this.promocodeForm.value,
      expiration_date: this.promocodeForm.value.expiration_date
        ? this.normalizeDateValue(this.promocodeForm.value.expiration_date)
        : ''
    };

    this.promoService.create(payload).subscribe({
      next: () => {
        this.resetForm();
        this.loadPromocodes();

        Swal.fire(
          this.transloco.translate('promocode.alerts.addedTitle'),
          '',
          'success'
        );
      },
      error: (err) => console.error(err)
    });
  }

  deleteItem(id: number): void {
    Swal.fire({
      title: this.transloco.translate('promocode.alerts.deleteTitle'),
      text: this.transloco.translate('promocode.alerts.deleteText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.transloco.translate('promocode.alerts.confirmDelete'),
      cancelButtonText: this.transloco.translate('promocode.alerts.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        this.promoService.delete(id).subscribe({
          next: () => {
            this.loadPromocodes();

            Swal.fire(
              this.transloco.translate('promocode.alerts.deletedTitle'),
              this.transloco.translate('promocode.alerts.deletedText'),
              'success'
            );
          },
          error: (err) => console.error(err)
        });
      }
    });
  }

  resetForm(): void {
    this.promocodeForm.reset({
      code: '',
      type: '1',
      percentage: '',
      expiration_date: '',
      active: true
    });

    this.closeExpirationCalendar();
    this.syncExpirationCalendarView();
  }

  loadMore(): void {
    if (this.contentReady || this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;

    this.currentPage++;

    const nextLength = this.currentPage * this.pageSize;
    this.visibleData = this.allData.slice(0, nextLength);

    this.hasMore = this.visibleData.length < this.allData.length;
    this.isLoadingMore = false;
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    const bottomReached = scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.loadMore();
    }
  }

  trackById(index: number, item: any): number {
    return item?.id ?? index;
  }

  /* ================================
     Expiration Calendar
     نفس Calendar بتاع New Booking
     ================================ */

  toggleExpirationCalendar(): void {
    this.showExpirationCalendar = !this.showExpirationCalendar;

    if (this.showExpirationCalendar) {
      const selectedDate = this.parseDateOnly(this.promocodeForm.get('expiration_date')?.value);
      const baseDate = selectedDate || new Date();

      this.expirationCalendarViewDate = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        1
      );
    }
  }

  closeExpirationCalendar(): void {
    this.showExpirationCalendar = false;
  }

  previousExpirationCalendarMonth(): void {
    this.expirationCalendarViewDate = new Date(
      this.expirationCalendarViewDate.getFullYear(),
      this.expirationCalendarViewDate.getMonth() - 1,
      1
    );
  }

  nextExpirationCalendarMonth(): void {
    this.expirationCalendarViewDate = new Date(
      this.expirationCalendarViewDate.getFullYear(),
      this.expirationCalendarViewDate.getMonth() + 1,
      1
    );
  }

  getExpirationCalendarTitle(): string {
    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      month: 'long',
      year: 'numeric',
    }).format(this.expirationCalendarViewDate);
  }

  getExpirationCalendarWeekDays(): string[] {
    return this.isAr
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }

  getExpirationCalendarDays(): ExpirationCalendarDay[] {
    const year = this.expirationCalendarViewDate.getFullYear();
    const month = this.expirationCalendarViewDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - firstDay.getDay());

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = this.parseDateOnly(this.promocodeForm.get('expiration_date')?.value);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      date.setHours(0, 0, 0, 0);

      return {
        date,
        dayNumber: this.isAr ? this.toArabicDigits(date.getDate()) : String(date.getDate()),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDate(date, today),
        isSelected: selectedDate ? this.isSameDate(date, selectedDate) : false,

        // سيبت كل الأيام متاحة عشان اللوجيك القديم في الموبايل مكنش مانع تاريخ قديم
        isDisabled: false
      };
    });
  }

  selectExpirationCalendarDate(date: Date): void {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    this.promocodeForm.patchValue({
      expiration_date: this.formatDateOnly(selectedDate)
    });

    this.promocodeForm.get('expiration_date')?.markAsTouched();
    this.promocodeForm.get('expiration_date')?.updateValueAndValidity();

    this.closeExpirationCalendar();
  }

  getExpirationDateDisplay(): string {
    const selectedDate = this.parseDateOnly(this.promocodeForm?.get('expiration_date')?.value);

    if (!selectedDate) {
      return this.isAr ? 'اختار تاريخ الانتهاء' : 'Select expiration date';
    }

    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(selectedDate);
  }

  private syncExpirationCalendarView(): void {
    const selectedDate = this.parseDateOnly(this.promocodeForm?.get('expiration_date')?.value);
    const baseDate = selectedDate || new Date();

    this.expirationCalendarViewDate = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      1
    );
  }

  private normalizeDateValue(value: any): string {
    if (!value) return '';

    const parsedDate = this.parseDateOnly(value);
    if (parsedDate) {
      return this.formatDateOnly(parsedDate);
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return this.formatDateOnly(date);
    }

    return String(value);
  }

  private parseDateOnly(value: any): Date | null {
    if (!value) return null;

    const cleanValue = String(value)
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0]
      .trim()
      .split(' ')[0];

    const match = cleanValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!match) return null;

    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  }

  private isSameDate(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
  }

  private formatDateOnly(date: Date): string {
    const pad = (num: number) => String(num).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private toArabicDigits(value: string | number): string {
    return String(value).replace(/\d/g, (digit) => '٠١٢٣٤٥٦٧٨٩'[Number(digit)]);
  }
}